"use client"

import { useMemo } from "react"

import { usePaymentMethods } from "@context/payment-methods-context"
import { formatCurrency } from "@lib/util/formatCurrency"

type InstallmentMessageProps = {
  finalPrice: number
  currency?: string | null
  className?: string
  textClassName?: string
}

const DEFAULT_INSTALLMENT_COUNT = 12

const normalizeInstallments = (value: unknown): number => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim().length > 0
        ? Number(value.trim())
        : Number.NaN

  if (!Number.isFinite(parsed)) return DEFAULT_INSTALLMENT_COUNT
  return Math.min(12, Math.max(1, Math.round(parsed)))
}

export default function InstallmentMessage({
  finalPrice,
  currency,
  className = "flex items-center gap-x-2",
  textClassName = "text-sm font-lealti-light text-zinc-800",
}: InstallmentMessageProps) {
  const { productInstallments } = usePaymentMethods()
  const normalizedPrice = Number(finalPrice)

  const { installments, baseMessage, installmentAmount } = useMemo(() => {
    const normalizedInstallments = normalizeInstallments(
      productInstallments?.installments
    )
    const message =
      typeof productInstallments?.message === "string" &&
      productInstallments.message.trim().length > 0
        ? productInstallments.message.replace(/\s+/g, " ").trim()
        : `Hasta ${normalizedInstallments} cuotas sin interés`

    return {
      installments: normalizedInstallments,
      baseMessage: message,
      installmentAmount:
        Number.isFinite(normalizedPrice) && normalizedPrice > 0
          ? normalizedPrice / normalizedInstallments
          : 0,
    }
  }, [
    normalizedPrice,
    productInstallments?.installments,
    productInstallments?.message,
  ])

  if (
    productInstallments?.enabled !== true ||
    !Number.isFinite(normalizedPrice) ||
    normalizedPrice <= 0 ||
    installments <= 0
  ) {
    return null
  }

  return (
    <div className={className}>
      <span className={textClassName}>
        {baseMessage} de {formatCurrency(installmentAmount, currency || "USD")}
      </span>
    </div>
  )
}
